/**
 * Which site a parking spot belongs to.
 *
 * This module imports NOTHING — same discipline as `rules.ts` and
 * `api/src/read/sql.ts`, so `scripts/verify-sites.mjs` executes the real
 * functions with no packages installed.
 *
 * ---------------------------------------------------------------------------
 * Why this exists at all
 *
 * The legacy database has one column for where a car is standing —
 * `reservation.parking` — and it holds seventy distinct strings that mean
 * five different places. The legacy booking form treats that as two places:
 * a Europort dropdown and one long "Tuchoměřice" list that quietly contains
 * POP, Kněževes and Šestka as well. `TUCHOMERICE_SPOTS` in rules.ts is a
 * faithful copy of that list and is therefore misnamed by inheritance.
 *
 * The legacy OPERATIONS screens do not agree with the form. Their Obsazenost
 * panel prints five headings — Tuchoměřice, POP, Kněževes, Šestka, Europort —
 * and their transfer strip offers "Z Tuchoměřic / Z POPu / Ze Šestky /
 * Z Kněževse". That is the grouping the dispatchers actually work in, and it
 * is what the owner asked for in the words "add other locations that we have:
 * POP, Knezeves, Tuchomerice, Sestka". So the five sites here are the
 * operational truth, not a new invention.
 *
 * ---------------------------------------------------------------------------
 * Why the folding happens in TypeScript and not in SQL
 *
 * A `GROUP BY` that had to classify these strings would need CASE arms over
 * seventy literals or LIKE prefixes, in SQL that has to be correct on both
 * MySQL 5.5 and SQLite. Instead every query groups by the raw column — which
 * both engines do identically and both engines index — and the fold happens
 * here, once, where it can be executed by a harness and read by a person.
 *
 * ---------------------------------------------------------------------------
 * Why an unrecognised spot is null rather than Tuchoměřice
 *
 * Tuchoměřice is the largest site and the tempting default. It is the wrong
 * default: a zone added in production next spring would then appear inside a
 * Tuchoměřice total that is silently too big, and nobody would ever find out.
 * Unrecognised returns null, the screens count it under "neurčeno", and the
 * number being visibly unattributed is what gets it fixed.
 */
export const PARKING_SITES = ['europort', 'tuchomerice', 'pop', 'knezeves', 'sestka'];
/** The headings, spelled as the legacy Obsazenost panel spells them. */
export const SITE_LABEL = {
    europort: 'Europort',
    tuchomerice: 'Tuchoměřice',
    pop: 'POP',
    knezeves: 'Kněževes',
    sestka: 'Šestka',
};
/**
 * The order the panel and the transfer strip list them in.
 *
 * Europort first because it is the garage at the terminal — the place a car
 * arrives at and leaves from, and therefore the one a dispatcher looks at
 * first. The rest follow in descending size, which is also the order the
 * legacy panel prints.
 */
export const SITE_ORDER = [
    'europort',
    'tuchomerice',
    'pop',
    'knezeves',
    'sestka',
];
/**
 * What the transfer queues call a leg to and from each site.
 *
 * The legacy strip says "Z Tuchoměřic", "Z POPu", "Ze Šestky", "Z Kněževse" —
 * genitive, and with the preposition changing to "ze" before Š. Both are
 * fixed strings here rather than anything generated: Czech genitive is not
 * derivable from a nominative by rule, and a screen that guesses at it reads
 * as broken to the people who have said these four words out loud every day
 * for ten years.
 */
export const SITE_FROM_LABEL = {
    europort: 'Z Europortu',
    tuchomerice: 'Z Tuchoměřic',
    pop: 'Z POPu',
    knezeves: 'Z Kněževse',
    sestka: 'Ze Šestky',
};
export const SITE_TO_LABEL = {
    europort: 'Na Europort',
    tuchomerice: 'Do Tuchoměřic',
    pop: 'Do POPu',
    knezeves: 'Do Kněževse',
    sestka: 'Na Šestku',
};
/**
 * The same four yards written as a leg — both ends named, Europort always one
 * of them, because Europort is the hub and every transfer starts or ends
 * there.
 *
 * The genitive labels above answer "where from?" and read correctly as a
 * filter chip. These answer "which run is this?" and read correctly as a tab,
 * which is a different question: a dispatcher looking at a tab strip wants to
 * see the whole journey, not the half of it the filter happens to key on.
 * The owner asked for this wording in exactly these words — "Knezeves-
 * Europort", "Europort - POP" — so both directions exist here even though
 * only one of them can currently be filtered (see TRANSFER_SITE_COL in
 * read/sql.ts: an outbound row does not record its destination yard, so
 * `LEG_FROM_EUROPORT` has no query behind it until phase 1 writes one).
 *
 * Nominative on both sides, so unlike the genitives these could have been
 * generated from SITE_LABEL. They are written out anyway: the strings a
 * dispatcher reads all day belong somewhere a person can edit them without
 * reading a template.
 *
 * `europort` maps to the bare name in both. It is never a leg endpoint on its
 * own — TRANSFER_SITES excludes it — but Record<ParkingSite, …> wants the key,
 * and "Europort → Europort" would be a worse thing to leave lying around.
 */
export const LEG_TO_EUROPORT = {
    europort: 'Europort',
    tuchomerice: 'Tuchoměřice → Europort',
    pop: 'POP → Europort',
    knezeves: 'Kněževes → Europort',
    sestka: 'Šestka → Europort',
};
export const LEG_FROM_EUROPORT = {
    europort: 'Europort',
    tuchomerice: 'Europort → Tuchoměřice',
    pop: 'Europort → POP',
    knezeves: 'Europort → Kněževes',
    sestka: 'Europort → Šestka',
};
/**
 * The Europort values that are not bare floor numbers.
 *
 * Kept as a literal rather than derived from EUROPORT_SPOTS so this module
 * stays importable on its own; `verify-sites.mjs` asserts the two agree.
 */
const EUROPORT_EXACT = ['outside_entry', 'outside_exit', 'M'];
/**
 * Prefixes, longest first where one is a prefix of another.
 *
 * "Před POPem" has to be tested before "POP" would ever be reached, and it is
 * — POP's own prefix is 'POP ' with the space, so 'Před POPem 2' cannot match
 * it. The space matters for a second reason: without it 'POPruhy', were such
 * a zone ever added, would silently become POP.
 */
const PREFIXES = [
    { prefix: 'Před POPem', site: 'pop' },
    /* The same place spelled in English — two cars stood on 'Before POP 1' and
       'Before POP 2' on the first production night, and the legacy zone table
       listed them under Tuchoměřice for want of a rule. They are POP's forecourt
       (the owner: "Before POP" → POP). */
    { prefix: 'Before POP', site: 'pop' },
    { prefix: 'POP ', site: 'pop' },
    { prefix: 'Kněževes', site: 'knezeves' },
    { prefix: 'Šestka', site: 'sestka' },
    { prefix: 'Hala', site: 'tuchomerice' },
    { prefix: 'Tráva', site: 'tuchomerice' },
    { prefix: 'Pod halou', site: 'tuchomerice' },
    { prefix: 'Pod recepcí', site: 'tuchomerice' },
    { prefix: 'Za halou', site: 'tuchomerice' },
    { prefix: 'Šterk', site: 'tuchomerice' },
    { prefix: 'Gravel', site: 'tuchomerice' },
    { prefix: 'Náměstí', site: 'tuchomerice' },
    { prefix: 'Vjezd', site: 'tuchomerice' },
];
/**
 * The site a raw `parking` / `parkingtarget` value belongs to, or null.
 *
 * Null covers three real cases and they are deliberately not told apart here:
 * the empty string (69 828 rows on staging — a car whose spot was never
 * recorded), a value this function has never heard of, and null itself.
 * All three mean the same thing to the person reading the screen: nobody
 * knows where that car is.
 */
export function siteOfSpot(spot) {
    if (spot === null || spot === undefined)
        return null;
    const s = spot.trim();
    if (s === '')
        return null;
    /* Bare digits are Europort floors. Stored as '7', not '7. patro' — the
       legacy form posted the option value and nothing ever normalised it. */
    if (/^\d+$/.test(s))
        return 'europort';
    if (EUROPORT_EXACT.indexOf(s) !== -1)
        return 'europort';
    for (const p of PREFIXES) {
        if (s.indexOf(p.prefix) === 0)
            return p.site;
    }
    /* A bare 'POP' with nothing after it, which staging does not have and a
       hand-typed row might. Tested after the prefixes so 'POP 3' never reaches
       it and 'Před POPem' never matches it. */
    if (s === 'POP')
        return 'pop';
    return null;
}
/** Convenience for the screens: the site's heading, or the honest fallback. */
export function siteLabelOf(spot) {
    const site = siteOfSpot(spot);
    return site === null ? 'Neurčeno' : SITE_LABEL[site];
}
/**
 * The LIKE prefixes that select one site's spots in SQL.
 *
 * Derived from PREFIXES above rather than typed out again, so a zone family
 * added to the classifier cannot be forgotten by the queries. The values are
 * bare prefixes — the caller appends '%' and supplies ESCAPE, because the
 * escape character is a property of the SQL layer, not of this module.
 *
 * Europort is deliberately EMPTY, and that is not an omission. Its spots are
 * bare floor numbers, and "a string of digits" is not expressible as a LIKE
 * prefix in SQL that has to run on both MySQL 5.5 and SQLite. It costs
 * nothing: Europort is where the transfers start and end, never a site a car
 * is fetched *from* by a transfer, so no query needs to name it this way.
 * `transferSitePrefixes()` refuses it out loud rather than returning an empty
 * list that would quietly match every row.
 *
 * One honest caveat: MySQL's default collation makes LIKE case-insensitive
 * while siteOfSpot is not, so in principle SQL could select a row the fold
 * then files elsewhere. verify:sql asserts against the real column that it
 * does not — the data has one spelling per zone.
 */
export const SITE_LIKE_PREFIXES = (() => {
    const out = {};
    for (const site of PARKING_SITES)
        out[site] = [];
    for (const p of PREFIXES)
        out[p.site].push(p.prefix);
    /* Europort's floor numbers are not prefixable; see above. */
    out.europort = [];
    return out;
})();
/**
 * The digits, as one-character LIKE prefixes.
 *
 * This is how "a bare floor number" gets expressed in SQL that has to be
 * correct on MySQL 5.5 and on SQLite. Neither dialect has a portable
 * character class — MySQL's REGEXP is not in SQLite, SQLite's GLOB is not in
 * MySQL — so "all digits" is not sayable. "Starts with a digit" is, as ten
 * LIKE prefixes, and over this column the two are the same set:
 *
 *   - every Europort floor value is digits and nothing else ('7', '13')
 *   - no other site's value begins with a digit; all thirteen prefixes in
 *     PREFIXES start with a letter
 *
 * Where they could come apart is a value like '3. patro' — digit-initial but
 * not digit-only, so SQL would file it under Europort and `siteOfSpot` would
 * file it under Neurčeno. That value does not exist: verify:sql walks every
 * distinct `parking` string in the database and asserts this clause and the
 * classifier agree on all of them, so the day somebody invents it, the suite
 * says so rather than the screen quietly miscounting.
 *
 * The earlier draft of this enumerated floors '1'…'30' as an IN list. That is
 * exact today and wrong the day a floor 31 opens — silently, with the cars on
 * it dropping out of the Europort filter and into Neurčeno. This version fails
 * in the direction that gets noticed instead.
 */
const DIGIT_PREFIXES = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
export const SITE_MATCH = (() => {
    const out = {};
    for (const site of PARKING_SITES) {
        out[site] = { exact: [], prefixes: SITE_LIKE_PREFIXES[site] };
    }
    out.europort = { exact: EUROPORT_EXACT, prefixes: DIGIT_PREFIXES };
    /* A bare 'POP' with nothing after it — the same case siteOfSpot handles
       after its prefix loop, and for the same reason. 'POP ' with the space
       cannot match it, so without this arm a hand-typed row would classify to
       POP on the screen and be missing from POP's own filter. */
    out.pop = { exact: ['POP'], prefixes: SITE_LIKE_PREFIXES.pop };
    return out;
})();
/**
 * What the Rezervace "Kde stojí" control offers, and what the URL carries.
 *
 * `none` is one of them and is not a site. It selects the rows `siteOfSpot`
 * returns null for — a blank column, or a value nobody has taught the
 * classifier — and it is offered because that bucket is real: 69 828 rows on
 * staging have no spot recorded at all. A filter that could show every site
 * but not that would let a dispatcher conclude the list is complete when it
 * is missing most of the database.
 */
export const SITE_FILTER_NONE = 'none';
export const SITE_FILTERS = [...SITE_ORDER, SITE_FILTER_NONE];
/** The dropdown's wording. 'Neurčeno' is the same word the panel prints. */
export const SITE_FILTER_LABEL = (() => {
    const out = {};
    for (const s of PARKING_SITES)
        out[s] = SITE_LABEL[s];
    out[SITE_FILTER_NONE] = 'Neurčeno';
    return out;
})();
/**
 * The match spec for one filter value, as `read/reservations.ts` needs it.
 *
 * `none` returns the union of all five sites with `negate` set, because "not
 * classified" is only sayable as "not any of these". The union is built here
 * rather than in the read model so that a site added to PARKING_SITES is
 * added to the negation in the same edit — forget it there and Neurčeno
 * quietly starts including a whole yard.
 */
export function siteFilterMatch(value) {
    if (value === SITE_FILTER_NONE) {
        const exact = [];
        const prefixes = [];
        for (const s of PARKING_SITES) {
            exact.push(...SITE_MATCH[s].exact);
            prefixes.push(...SITE_MATCH[s].prefixes);
        }
        return { exact, prefixes, negate: true };
    }
    const m = SITE_MATCH[value];
    return m ? { exact: m.exact, prefixes: m.prefixes } : null;
}
/**
 * The prefixes for a site a transfer can run to or from, or a thrown error.
 *
 * Throwing is the point. A caller that asks for Europort's prefixes has asked
 * a question with no answer, and the alternative — an empty array — builds a
 * WHERE clause that is vacuously true and hands the dispatcher every car in
 * the company under the heading "Z Europortu".
 */
export function transferSitePrefixes(site) {
    const p = SITE_LIKE_PREFIXES[site];
    if (!p || p.length === 0) {
        throw new Error(`no LIKE prefixes for site '${site}' — its spots are not prefix-matchable, ` +
            'so it cannot be used as a transfer endpoint filter');
    }
    return p;
}
/** The four sites a car is transferred to and from. Europort is the hub. */
export const TRANSFER_SITES = SITE_ORDER.filter((s) => s !== 'europort');
/**
 * What a zone is called when the column was left blank.
 *
 * The fold turns `null` and `''` into this string so the panel has something
 * to print, and it stays a named constant rather than a literal because it is
 * no longer only a label: clicking a zone row asks the server for the cars
 * standing in it, and this is the one value that does not mean "the column
 * holds these characters". The read model recognises it and asks for the
 * blanks instead, so a card headed "Neurčeno" opens as well as any other. A
 * literal repeated in two packages would come apart the day somebody improved
 * the wording.
 */
export const NO_ZONE = '(bez místa)';
export function foldBySite(rows) {
    const by = new Map();
    for (const site of SITE_ORDER) {
        by.set(site, { site, label: SITE_LABEL[site], total: 0, zones: [] });
    }
    /* The unattributed bucket is created only if something lands in it. An
       empty "Neurčeno 0" heading on every screen would train people to ignore
       the one place this panel admits it does not know something.
  
       Its key is a NUL and a word, written below as an escape rather than as
       the byte itself. It was the byte until 26 July, which made this a file
       git and grep both classified as binary — "Binary files differ" in place
       of a diff, on the one module that decides which yard every car on the
       screen belongs to. The value is unchanged, and so is the reason for it:
       no `ParkingSite` can start with a NUL, so this bucket cannot collide
       with a real one however the list of sites grows. */
    for (const r of rows) {
        const site = siteOfSpot(r.zone);
        const key = site === null ? '\u0000none' : site;
        let bucket = by.get(key);
        if (!bucket) {
            bucket = { site: null, label: 'Neurčeno', total: 0, zones: [] };
            by.set(key, bucket);
        }
        bucket.total += r.count;
        const zone = (r.zone ?? '').trim();
        bucket.zones.push({ zone: zone === '' ? NO_ZONE : zone, count: r.count });
    }
    const out = [];
    for (const site of SITE_ORDER) {
        const b = by.get(site);
        if (b)
            out.push(b);
    }
    const none = by.get('\u0000none');
    if (none)
        out.push(none);
    /* Zones descending by count: the full ones are what a dispatcher deciding
       where to send the next car is looking for, and an alphabetical list buries
       them among the empty ones. Ties break on the zone name so the order is
       stable between refreshes rather than dependent on row order. */
    for (const b of out) {
        b.zones.sort((x, y) => (y.count - x.count) || (x.zone < y.zone ? -1 : x.zone > y.zone ? 1 : 0));
    }
    return out;
}
